import { useEffect, useRef, useState } from 'react';

/** The one auto-refresh cadence: every live page and the known-users list poll at this rate. */
export const REFRESH_MS = 30_000;

const storageKey = (page: string) => `toolbox:auto-refresh:${page}`;

const readStored = (page: string) => {
  try {
    return localStorage.getItem(storageKey(page)) !== 'off';
  } catch {
    return true;
  }
};

/**
 * Auto refresh for a live page: on until switched off, and the choice is remembered per `page`, and `refresh` runs
 * every `REFRESH_MS` only while the tab is visible.
 */
export function useAutoRefresh(page: string, refresh: () => void) {
  const [auto, setAutoState] = useState(() => readStored(page));
  const [visible, setVisible] = useState(() => document.visibilityState === 'visible');
  const latest = useRef(refresh);

  useEffect(() => {
    latest.current = refresh;
  });

  useEffect(() => {
    const onChange = () => setVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);

  useEffect(() => {
    if (!auto || !visible) return;
    const timer = window.setInterval(() => latest.current(), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [auto, visible]);

  const setAuto = (next: boolean) => {
    setAutoState(next);
    try {
      localStorage.setItem(storageKey(page), next ? 'on' : 'off');
    } catch {
      // Storage can be blocked; the choice then lasts for this visit only.
    }
  };

  return { auto, setAuto };
}
